import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { fetchProjects } from '../services/ProjectService';
import './AlbumSelectionPage.css';
import { useNavigate } from 'react-router-dom';
import AlbumListPanel from '../components/AlbumListPanel';
import type { Album } from './AlbumListPanel';
import { jwtDecode } from'jwt-decode';
import { MessageDialog } from '../components/MessageDialog';
import { useLocation } from "react-router-dom";
import { Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography} from '@mui/material';


interface TokenPayload {
    username: string;
    id?: string;
    exp?: number;
}

interface AlbumSelectionPageProps {
    onSelect: (
        albumType: string,
        albumSize: { width: number; height: number; unit: 'inches' },
        pageCount: number,
        backgroundColor: string,
        albumName: string,
        cutArea: number,
        unit: 'inches',// pass the unit directly
        spineWidth?: number,
    ) => void;
    onOpenProject: (projectId: string) => void;
}


export default function AlbumSelectionPage({ onSelect, onOpenProject }: AlbumSelectionPageProps) {
    const [selectedProduct, setSelectedProduct] =
        useState<keyof typeof PRODUCTS>('BOUTIQUE');
 
    // Step 2: Initialize unit from localStorage or default
    const [unit, setUnit] = useState<'inches'>('inches');

    const { token, logout } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');  
    const [projects, setProjects] = useState<any[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [selectedPageCount, setSelectedPageCount] = useState<number>(10);
    const [backgroundColor, setBackgroundColor] = useState('#ffffff');
    const [albumName, setAlbumName] = useState('');
    const [cutArea, setCutArea] = useState<number>(0.3);
    const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [albumNameError, setAlbumNameError] = useState("");
  
   
    const [spineWidth, setSpineWidth] = useState<number>(0);
    const location = useLocation();
    const API_URL = process.env.REACT_APP_API_URL; 
  

    useEffect(() => {
        if (!token) return;
        setLoadingProjects(true);
        fetchProjects(token)
            .then(setProjects)
            .catch(console.error)
            .finally(() => setLoadingProjects(false));
    }, [token]);

    //get the login info
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode<TokenPayload>(token);
                setUsername(decoded.username);
            } catch (err) {
                console.error('Invalid token', err);
            }
        }
    }, []);



    type Orientation = 'square' | 'vertical' | 'horizontal';

    interface ProductConfig {
        square?: Size[];
        vertical?: Size[];
        horizontal?: Size[];      
    }

    const [selectedOrientation, setSelectedOrientation] = useState<Orientation>('square');
    const [selectedSizeIndex, setSelectedSizeIndex] = useState<number>(0);

    interface Size {
        width: number;
        height: number;
    }

    const CANVAS_SIZES: ProductConfig = {
        square: [
            { width: 10, height: 10 },
            { width: 12, height: 12 },
            { width: 14, height: 14 },
            { width: 16, height: 16 },
            { width: 20, height: 20 },
            { width: 24, height: 24 },
            { width: 30, height: 30 },
            { width: 40, height: 40 },
        ],
        vertical: [
            { width: 10, height: 14 },
            { width: 11, height: 14 },
            { width: 16, height: 20 },
            { width: 16, height: 24 },
            { width: 20, height: 24 },
            { width: 20, height: 30 },
            { width: 24, height: 36 },
            { width: 28, height: 42 },
            { width: 30, height: 40 },
            { width: 32, height: 48 },
            { width: 36, height: 54 },
        ],
        horizontal: [
            { width: 14, height: 10 },
            { width: 14, height: 11 },
            { width: 20, height: 16 },
            { width: 24, height: 16 },
            { width: 24, height: 20 },
            { width: 30, height: 20 },
            { width: 36, height: 24 },
            { width: 42, height: 28 },
            { width: 40, height: 30 },
            { width: 48, height: 32 },
            { width: 54, height: 36 },
        ],
    };




    const PRODUCTS: Record<string, ProductConfig> = {
        BOUTIQUE: {
            square: [
                { width: 12, height: 12 },
                { width: 10, height: 10 },
                { width: 8, height: 8 },
                { width: 6, height: 6 },
            ],
            vertical: [
                { width: 11, height: 14 },
                { width: 10, height: 14 },
                { width: 9, height: 12 },
                { width: 8, height: 12 },
                { width: 8, height: 10 },
                { width: 7, height: 10 },
                { width: 5.5, height: 7},
                { width: 5, height: 7 },
            ],
            horizontal: [
                { width: 14, height: 11 },
                { width: 14, height: 10 },
                { width: 12, height: 9 },
                { width: 12, height: 8 },
                { width: 10, height: 8 },
                { width: 10, height: 7 },
                { width: 7, height: 5.5},
                { width: 7, height: 5 },
            ],

        },
        LIFESTYLE: {
            square: [{ width: 10, height: 10 }],
            vertical: [
                { width: 10, height: 14 },
                { width: 9, height: 12 },
            ],

        },
        PORTRAIT: {
            square: [{ width: 10, height: 10 }],
            vertical: [
                { width: 9, height: 12 },
                { width: 8, height: 12 },
            ],
            horizontal: [
                { width: 12, height: 9 },
                { width: 12, height: 8 },
            ],
   
        },
        JOURNAL: {
            square: [
                { width: 12, height: 12 },
                { width: 10, height: 10 },
            ],
            vertical: [
                { width: 11, height: 14 },
                { width: 9, height: 12 },
            ],

        },
        GUEST: {
            square: [
                { width: 10, height: 10 },
                { width: 8, height: 8 },
            ],
            vertical: [
                { width: 8, height: 12 },
                { width: 8, height: 10 },
            ],
            horizontal: [
                { width: 12, height: 8 },
                { width: 10, height: 8 },
            ],
        },

        LITE: {
            square: [
                { width: 10, height: 10 },
                { width: 7, height: 7 },
                { width: 5, height: 5 },
                { width: 4, height: 4 },
            ],
            vertical: [
                { width: 8, height: 10 },
                { width: 5, height: 7 },
                { width: 4, height: 5 },
            ],
            horizontal: [
                { width: 10, height: 8 },
                { width: 7, height: 5 },              
            ],
        },

        LUXE: {
            vertical: [
                { width: 7.5, height: 10 },
                { width: 5.25, height: 7 },

            ],
        },
        FPRINTS: {
            vertical: [
                { width: 8, height: 10 },
                { width: 11, height: 14 },
                { width: 12, height: 16 },
                { width: 16, height: 20 },
                { width: 16, height: 24 },
                { width: 20, height: 24 },
                { width: 20, height: 30 },
                { width: 24, height: 34 },
                { width: 30, height: 40 },
            ],

            horizontal: [
                { width: 10, height: 8 },
                { width: 14, height: 11 },
                { width: 16, height: 12 },
                { width: 20, height: 16 },
                { width: 24, height: 16 },
                { width: 24, height: 20 },
                { width: 30, height: 20 },
                { width: 34, height: 24 },
                { width: 40, height: 30 },
            ],

            square: [
                { width: 10, height: 10 },
                { width: 12, height: 12 },
                { width: 14, height: 14 },
                { width: 16, height: 16 },
                { width: 20, height: 20 },
                { width: 24, height: 24 },
                { width: 30, height: 30 },
                { width: 40, height: 40 },
            ],
        },
        ARCH: {
            vertical: [
                { width: 6, height: 8 },
                { width: 8, height: 10 },
                { width: 11, height: 14 },
                { width: 12, height: 16 },
                { width: 16, height: 20 },
                { width: 16, height: 24 },
                { width: 20, height: 24 },
                { width: 20, height: 30 },
                { width: 24, height: 34 },
                { width: 30, height: 40 },
            ],

        },
        HANDTORN: {
            vertical: [
                { width: 6, height: 8 },
                { width: 8, height: 10 },
                { width: 11, height: 14 },
                { width: 12, height: 16 },
                { width: 16, height: 20 },
                { width: 16, height: 24 },
                { width: 20, height: 24 },
                { width: 20, height: 30 },
                { width: 24, height: 34 },
                { width: 30, height: 40 },
            ],

            horizontal: [
                { width: 8, height: 6 },
                { width: 10, height: 8 },
                { width: 14, height: 11 },
                { width: 16, height: 12 },
                { width: 20, height: 16 },
                { width: 24, height: 16 },
                { width: 24, height: 20 },
                { width: 30, height: 20 },
                { width: 34, height: 24 },
                { width: 40, height: 30 },
            ],

            square: [
                { width: 8, height: 8 },
                { width: 10, height: 10 },
                { width: 12, height: 12 },
                { width: 14, height: 14 },
                { width: 16, height: 16 },
                { width: 20, height: 20 },
                { width: 24, height: 24 },
                { width: 30, height: 30 },
                { width: 40, height: 40 },
            ],
        },


        MONTAGE: {
            square: [
                { width: 20, height: 20 },
                { width: 30, height: 30 },
                { width: 40, height: 40 },
            ],
            horizontal: [
                { width: 24, height: 12 },
                { width: 30, height: 15 },
                { width: 36, height: 18 },
                { width: 40, height: 20 },
            ],

        },
        CANVAS: CANVAS_SIZES,
        FCANVAS: CANVAS_SIZES,
     
        PRINTS: {
            square: [
                { width: 5, height: 5 },
                { width: 7, height: 7 },
                { width: 8, height: 8 },
                { width: 10, height: 10 },
                { width: 12, height: 12 },
            ],
            vertical: [
                { width: 2.5, height: 3.5 },
                { width: 3.5, height: 5 },
                { width: 4, height: 5 },
                { width: 4, height: 6 },
                { width: 5, height: 7 },
                { width: 5, height: 7.5 },
                { width: 6, height: 8 },
                { width: 6, height: 9 },
                { width: 7, height: 10 },
                { width: 8, height: 10 },
                { width: 8, height: 12 },
                { width: 10, height: 14 },
                { width: 11, height: 14 },
                { width: 12, height: 16 },
                { width: 12, height: 18 },
                { width: 16, height: 20 },
                { width: 16, height: 24 },
                { width: 20, height: 24 },
                { width: 20, height: 30 },
                { width: 20, height: 32 },
                { width: 24, height: 30 },
                { width: 24, height: 36 },
                { width: 30, height: 40 },
                { width: 32, height: 40 },
            ],
            horizontal: [
                { width: 3.5, height: 2.5 },
                { width: 5, height: 3.5 },
                { width: 5, height: 4 },
                { width: 6, height: 4 },
                { width: 7, height: 5 },
                { width: 7.5, height: 5 },
                { width: 8, height: 6 },
                { width: 9, height: 6 },
                { width: 10, height: 7 },
                { width: 10, height: 8 },
                { width: 12, height: 8 },
                { width: 14, height: 10 },
                { width: 14, height: 11 },
                { width: 16, height: 12 },
                { width: 18, height: 12 },
                { width: 20, height: 16 },
                { width: 24, height: 16 },
                { width: 24, height: 20 },
                { width: 30, height: 20 },
                { width: 30, height: 24 },
                { width: 32, height: 20 },
                { width: 36, height: 24 },
                { width: 40, height: 30 },
                { width: 40, height: 32 },
            ],
        },
        HIPSTER: {
            vertical: [
                { width: 4, height: 4.75 },
            ],
        },
        FMOUNTS: {
            vertical: [
                { width: 8, height: 10 },
                { width: 11, height: 14 },
            ],

            horizontal: [
                { width: 10, height: 8 },
                { width: 14, height: 11 },
            ],
        },
      

       
    };

    const product = PRODUCTS[selectedProduct];

    const orientationOptions: Orientation[] = (() => {
        const opts: Orientation[] = [];
        if (product.square && product.square.length > 0) opts.push('square');
        if (product.vertical && product.vertical.length > 0) opts.push('vertical');
        if (product.horizontal && product.horizontal.length > 0) opts.push('horizontal');
        return opts;
    })();

    // Returns the sizes for the selected orientation
    function getSizes(product: ProductConfig, selectedOrientation: Orientation): Size[] {
        if (selectedOrientation === 'square') return product.square || [];
        if (selectedOrientation === 'vertical') return product.vertical || [];
        if (selectedOrientation === 'horizontal') return product.horizontal || [];
        return [];
    }


    // Define min spreads per product
    const MIN_SPREADS: Record<string, number> = {
        BOUTIQUE: 10,
        LIFESTYLE: 10,
        PORTRAIT: 6,
        JOURNAL: 10,
        GUEST: 6,
        LITE: 6,
        LUXE: 10,
        PRINTS: 1,
        FMOUNTS: 10,
        HANDTORN: 1,
        FPRINTS: 1,
        HIPSTER:10,
    };

    const MAX_SPREADS: Record<string, number> = {
        BOUTIQUE: 45,
        LIFESTYLE: 40,
        PORTRAIT: 20,
        JOURNAL: 45,
        GUEST: 15,
        LITE: 20,
        LUXE:20,
        PRINTS: 45,
        HIPSTER: 25,
        FMOUNTS: 25,
    };

    const minSpreads = MIN_SPREADS[selectedProduct] || 6; // fallback just in case
    const maxSpreads = MAX_SPREADS[selectedProduct] || 15;

    const NO_SPREAD_PRODUCTS = new Set([
        "MONTAGE",
        "CANVAS",
        "FCANVAS",
        "ARCH",
        "FPRINTS",
        "HANDTORN",
    ]);
    function handleOpenProject(album: Album) {
        setSelectedAlbum(album);
        navigate(`/album/${album._id}`);
    }


    const NO_COLOR_ENABLE = new Set([
        "MONTAGE",
        "CANVAS",
        "FCANVAS",
        "ARCH",
        "FPRINTS",
        "HANDTORN",
        "PRINTS",
        "HIPSTER",
        "FMOUNTS"
    ]);

    //logout code
    const handleLogout = () => {
        logout(); // clear token + localStorage
        navigate('/login'); // go back to login page
    };

    function onAlbumSaved() {
        setRefreshTrigger(prev => prev + 1);  // triggers AlbumListPanel to refetch
    }

    useEffect(() => {
        if (location.state?.albumSaved) {
            onAlbumSaved(); //  this triggers your refresh
            // Clear it so it doesn’t repeat on reload
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);



    //check the album Name for wrong characters
    function isValidAlbumName(name: string) {
        return /^[\p{L}\p{N}_-]+(?: [\p{L}\p{N}_-]+)*$/u.test(name);
    }
    const SELECTED_COLOR = '#59b3d8';

    const goToHelp = () => {
        window.open(
            "https://easydigitalalbum.com/help-center_seldex/",
            "_blank",
            "noopener,noreferrer"
        );
    };


    function formatSize(size: number): string {
        return size.toFixed(3); // 3 decimal places
    }

    const getProductStep = (product: string) => {
        if (product === "HIPSTER") return 5;
        return 1;
    };

    const normalizePageCount = (product: string, value: number) => {
        if (product === "HIPSTER") {
            return Math.round(value / 5) * 5;
        }

        if (product === "FMOUNTS") {
            return value <= 17 ? 10 : 25;
        }

        return value;
    };




    return (
        <div
            className="album-selection-page"
            onContextMenu={(e) => e.preventDefault()}
        >

        {/*TOP HEADER*/}
        <div className="page-container">
        <div className="album-selection-page">
                    <header className="top-bar">

                        <button className="help-button" onClick={goToHelp}>
                            <img src={`${process.env.PUBLIC_URL}/help_center_button.png`} alt="Help" />
                        </button>

                <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="EDA" className="eda-logo" />
                <img src={`${process.env.PUBLIC_URL}/client_logo.png`} alt="Client" className="client-logo" />
                <div className="user-section">
                        <img src={`${process.env.PUBLIC_URL}/user_login.png`} alt="Online" className="status-icon" />
                        <input
                            type="text"
                            title="usernmae"
                            value={username}
                            readOnly
                            className="username-field"
                        />
                    {/* Logout button */}
                    <Tooltip title="Logout" arrow>
                        <span>
                    <button className="logout-button" onClick={handleLogout}>
                              <img src={`${process.env.PUBLIC_URL}/user_logout.png`} alt="Logout" />
                        </button>
                        </span>
                    </Tooltip>
                </div>
            </header>


           {/* TOP WHITE WRAPPER*/}
            <main className="main-content">   
                <section className="top-white-wrapper">
                    <h2 className="section-title">Create New Product</h2>

                            <div className="form-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                                {/* Album Name + Units */}                             
                                <div
                                    className="form-row"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "70px", // space BETWEEN the 2 groups
                                    }}
                                >
                                    {/* GROUP 1: Album + Units */}
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <label htmlFor="album-name">Studio Reference:</label>

                                        <input
                                            id="album-name"
                                            type="text"
                                            value={albumName}
                                            placeholder={albumNameError || "Studio reference"}
                                            required
                                            onChange={(e) => {
                                                setAlbumName(e.target.value);
                                                setAlbumNameError("");
                                            }}

                                            className="form-input"
                                            style={{
                                                borderColor: albumNameError ? "red" : undefined,
                                                backgroundColor: albumNameError ? "#ffe6e6" : undefined,
                                            }}
                                            title={albumNameError || undefined} // tooltip shows the error
                                        />


                                        <label htmlFor="units" style={{ marginLeft: 10 }}>Units:</label>
                                        <select
                                            id="units"
                                            className="form-select"
                                            value="inches"
                                            onChange={() => setUnit("inches")}
                                            style={{ height: 32, minWidth: 65 }}
                                        >
                                            <option value="inches">inches</option>
                                        </select>
                                    </div>

                                    
                                </div>

                                {/* Product Category Buttons */}
                                <div
                                    style={{
                                        fontWeight: 600,                                   
                                        fontSize: 15,
                                        color: "#59b3d8",
                                        marginBottom: -15,
                                        marginTop:-5,
                                        marginLeft: 5, //  make sure it's aligned left
                                        alignSelf: "flex-start", //  this is the key
                                    }}
                                >
                                    Albums
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                        marginTop: 5,
                                        marginLeft: -5,
                                        alignItems: "flex-start",
                                        position: "relative"
                                    }}
                                >
                                    {/* ---------------------- Albums Section ---------------------- */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(7, 120px)",
                                            gridAutoRows: "68px",
                                            gap: "20px",
                                        }}
                                    >
                                        {Object.keys(PRODUCTS).slice(0, 7).map((productKey) => {
                                            const productName =
                                                productKey === "BOUTIQUE"
                                                    ? "Boutique Book"
                                                    : productKey === "LIFESTYLE"
                                                        ? "Lifestyle Book"
                                                        : productKey === "PORTRAIT"
                                                            ? "Portrait Book"
                                                            : productKey === "JOURNAL"
                                                                ? "Journal"
                                                                : productKey === "GUEST"
                                                                    ? "Guest Book"
                                                                    : productKey === "LITE"
                                                                        ? "Lite Book"
                                                                        : productKey === "LUXE"
                                                                            ? "Luxe Album"
                                                                        : productKey;


                                            return (
                                                <button
                                                    key={productKey}
                                                    type="button"
                                                    onClick={() => {
                                                        const minSpreadsForCategory = MIN_SPREADS[productKey] || 10;
                                                        setSelectedProduct(productKey);
                                                        setSelectedOrientation("vertical");
                                                        setSelectedSizeIndex(0);
                                                        setSelectedPageCount(minSpreadsForCategory);
                                                    }}
                                                    style={{
                                                        width: 126,
                                                        height: 43,
                                                        border: selectedProduct === productKey ? "2px solid #59b3d8" : "2px solid #ccc",
                                                        backgroundColor: "#fff",
                                                        cursor: "pointer",
                                                        borderRadius: 10,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 600,
                                                        fontSize: 14.5,
                                                        color: selectedProduct === productKey ? SELECTED_COLOR : "#969696",
                                                        boxShadow: selectedProduct === productKey ? "0 4px 10px rgba(0,0,0,0.25)" : "none",
                                                    }}
                                                >
                                                    {productName}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* ---------------------- Wall Art Section ---------------------- */}
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                            color: "#59b3d8",
                                            marginTop: -25,
                                            marginLeft: 5,
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        Wall Art
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(7, 120px)",
                                            gridAutoRows: "68px",
                                            gap: "20px",
                                            marginLeft: 0,
                                            marginTop: -5,
                                        }}
                                    >
                                        {Object.keys(PRODUCTS).slice(7, 13).map((productKey) => {
                                            const productName =
                                                productKey === "FPRINTS"
                                                    ? "Framed Prints"
                                                    : productKey === "ARCH"
                                                        ? "Arch Frames"
                                                        : productKey === "HANDTORN"
                                                            ? "Hand Torn Frames"
                                                            : productKey === "MONTAGE"
                                                                ? "Montage Frames"
                                                                : productKey === "CANVAS"
                                                                    ? "Canvas"
                                                                    : productKey === "FCANVAS"
                                                                        ? "Framed Canvas"
                                                                            : productKey;


                                            return (
                                            <button
                                                key={productKey}
                                                type="button"
                                                    onClick={() => {
                                                        const product = PRODUCTS[productKey];

                                                        const firstOrientation =
                                                            (product.square && product.square.length > 0) ? "square" :
                                                                (product.vertical && product.vertical.length > 0) ? "vertical" :
                                                                    (product.horizontal && product.horizontal.length > 0) ? "horizontal" :
                                                                        "square";

                                                        setSelectedProduct(productKey);
                                                        setSelectedOrientation(firstOrientation);
                                                        setSelectedSizeIndex(0);
                                                        setSelectedPageCount(1);
                                                    }}
                                                style={{
                                                    width: 126,
                                                    height: 43,
                                                    border: selectedProduct === productKey ? "2px solid #59b3d8" : "2px solid #ccc",
                                                    backgroundColor: "#fff",
                                                    cursor: "pointer",
                                                    borderRadius: 10,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 600,
                                                    fontSize: 14.5,
                                                    color: selectedProduct === productKey ? SELECTED_COLOR : "#969696",
                                                    boxShadow: selectedProduct === productKey ? "0 4px 10px rgba(0,0,0,0.25)" : "none",
                                                }}
                                            >
                                                    {productName}
                                                </button>
                                            );
                                        })}
                                    </div>


                                    {/* ---------------------- Prints Section ---------------------- */}
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                            color: "#59b3d8",
                                            marginTop: -25,
                                            marginLeft: 5,
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        Prints
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(7, 120px)",
                                            gridAutoRows: "68px",
                                            gap: "20px",
                                            marginLeft: 0,
                                            marginTop: -5,
                                        }}
                                    >
                                        {Object.keys(PRODUCTS).slice(13, 15).map((productKey) => {
                                            const minSpreadsForCategory = MIN_SPREADS[productKey] || 10;
                                            const productName =
                                                productKey === "PRINTS"
                                                    ? " Prints"
                                                    : productKey === "HIPSTER"
                                                        ? "Hipster Packs"
                                                                        : productKey;

                                            return (
                                                <button
                                                    key={productKey}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedProduct(productKey);
                                                        setSelectedOrientation("vertical");
                                                        setSelectedSizeIndex(0);
                                                        setSelectedPageCount(minSpreadsForCategory);
                                                    }}
                                                    style={{
                                                        width: 126,
                                                        height: 43,
                                                        border: selectedProduct === productKey ? "2px solid #59b3d8" : "2px solid #ccc",
                                                        backgroundColor: "#fff",
                                                        cursor: "pointer",
                                                        borderRadius: 10,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 600,
                                                        fontSize: 14.5,
                                                        color: selectedProduct === productKey ? SELECTED_COLOR : "#969696",
                                                        boxShadow: selectedProduct === productKey ? "0 4px 10px rgba(0,0,0,0.25)" : "none",
                                                    }}
                                                >
                                                    {productName}
                                                </button>
                                            );
                                        })}
                                    </div>


                                    {/* ---------------------- FMounts ---------------------- */}
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                            color: "#59b3d8",
                                            marginTop: -25,
                                            marginLeft: 5,
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                       Frame Mounts
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(7, 120px)",
                                            gridAutoRows: "68px",
                                            gap: "20px",
                                            marginLeft: 0,
                                            marginTop: -5,
                                        }}
                                    >
                                        {Object.keys(PRODUCTS).slice(15, 16).map((productKey) => {
                                            const minSpreadsForCategory = MIN_SPREADS[productKey] || 10;
                                            const productName =
                                                productKey === "FMOUNTS"
                                                   ? " Frame Mounts"                                               
                                                        : productKey;

                                            return (
                                                <button
                                                    key={productKey}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedProduct(productKey);
                                                        setSelectedOrientation("vertical");
                                                        setSelectedSizeIndex(0);
                                                        setSelectedPageCount(minSpreadsForCategory);
                                                    }}
                                                    style={{
                                                        width: 126,
                                                        height: 43,
                                                        border: selectedProduct === productKey ? "2px solid #59b3d8" : "2px solid #ccc",
                                                        backgroundColor: "#fff",
                                                        cursor: "pointer",
                                                        borderRadius: 10,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 600,
                                                        fontSize: 14.5,
                                                        color: selectedProduct === productKey ? SELECTED_COLOR : "#969696",
                                                        boxShadow: selectedProduct === productKey ? "0 4px 10px rgba(0,0,0,0.25)" : "none",
                                                    }}
                                                >
                                                    {productName}
                                                </button>
                                            );
                                        })}
                                    </div>                   
                                    </div>
                                 
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        width: '100%',
                                        maxWidth: 1100,
                                        marginTop: -20,
                                    }}
                                >
                                
                                    {/* LEFT SIDE */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 16,                                         
                                           // alignItems: 'flex-start',
                                            padding: '24px 32px',
                                            borderRadius: 12,
                                            border: '1px solid #cccccc',
                                            width: 479,
                                            backgroundColor: '#fff',
                                            marginTop: 4,
                                            marginLeft:-4,
                                        }}
                                    >
                                        {/* Top Row: Orientation (left) + Size (right) */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                            {/* Orientation */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <label style={{ cursor: 'default'}}>Orientation:</label>
                                                <select
                                                    value={selectedOrientation}
                                                    className="form-input-small"
                                                    onChange={(e) => setSelectedOrientation(e.target.value as Orientation)}
                                                    disabled={orientationOptions.length <= 1}
                                                    style={{ height: 34, width: 120 }}
                                                >
                                                    {orientationOptions.map((o) => (
                                                        <option key={o} value={o}>{o}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Size */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <label style={{ cursor: 'default' , paddingLeft:38 }}>Size:</label>
                                                <select
                                                    value={selectedSizeIndex}
                                                    className="form-input"
                                                    onChange={(e) => setSelectedSizeIndex(Number(e.target.value))}
                                                    style={{ height: 34, width: 150, marginRight:5}}
                                                >
                                                    {getSizes(PRODUCTS[selectedProduct], selectedOrientation).map((s, idx) => (
                                                        <option key={idx} value={idx}>
                                                            {s.width} x {s.height} inches
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Bottom Row: Spreads (left) + Background (right) */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                            {/* Spreads */}                                 
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 0}}>
                                                <label style={{ cursor: 'default' }}>Spreads:</label>
                                                <input
                                                    disabled={NO_SPREAD_PRODUCTS.has(selectedProduct)}
                                                    type="number"
                                                    className="form-spreads"
                                                    min={minSpreads}
                                                    max={maxSpreads}
                                                    step={selectedProduct === "HIPSTER" ? 5 : 1}
                                                    value={selectedPageCount}
                                                    onChange={(e) => {
                                                        let value = Number(e.target.value);

                                                        if (Number.isNaN(value)) return;

                                                        if (value > maxSpreads) {
                                                            value = maxSpreads;
                                                        }

                                                        setSelectedPageCount(value);
                                                    }}
                                                    onBlur={() => {
                                                        let value = selectedPageCount;

                                                        if (value < minSpreads) value = minSpreads;
                                                        if (value > maxSpreads) value = maxSpreads;

                                                        value = normalizePageCount(selectedProduct, value);

                                                        setSelectedPageCount(value);
                                                    }}
                                                />
                                            </div>

                                            {/* Background Color */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <label style={{ cursor: 'default' }}>Background Color:</label>
                                                <input
                                                    disabled={NO_COLOR_ENABLE.has(selectedProduct)}
                                                    type="color"
                                                    value={backgroundColor}
                                                    onChange={(e) => setBackgroundColor(e.target.value)}
                                                    style={{ height: 26, width: 40 , marginRight:5}}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* RIGHT SIDE - CREATE BUTTON */}
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#9f9f9f',
                                                fontWeight: 700,
                                                height: 40,
                                                marginTop: 12,
                                                marginRight:-0.9,
                                                width: 180 ,
                                                textTransform: 'none',
                                                '&:hover': { backgroundColor: '#59b3d8' },
                                            }}
                                            onClick={async () => {
                                            
                                                try {
                                                    // Reset errors
                                                    setAlbumNameError('');

                                                    // --- Validate album name ---
                                                    if (!albumName.trim()) {
                                                        setAlbumNameError('Studio reference is required');
                                                        return;
                                                    }
                                                    if (!isValidAlbumName(albumName)) {
                                                        setAlbumNameError('Studio reference can only contain letters, numbers, _ and -');
                                                        return;
                                                    }
                                                

                                                    // --- Check duplicate album name ---
                                                    const token = localStorage.getItem('token');
                                                    const response = await fetch(
                                                        `${API_URL}/albums/check-name?name=${encodeURIComponent(albumName)}`,
                                                        { headers: { Authorization: `Bearer ${token}` } }
                                                    );

                                                    if (!response.ok) throw new Error('Failed to validate Studio reference name');

                                                    const data = await response.json();
                                                    if (data.exists) {
                                                        setAlbumNameError('Product already exists');
                                                        return;
                                                    }

                           
                                                    // --- Determine album size ---
                                                    const sizes = getSizes(product, selectedOrientation);
                                                    const selectedSize = sizes[selectedSizeIndex];
                                                    if (!selectedSize) {
                                                        setDialogMessage('Invalid album size');
                                                        setDialogOpen(true);
                                                        return;
                                                    }
                                               
                                                    const parsedSize =
                                                        selectedProduct === "CANVAS" || selectedProduct === "FCANVAS"
                                                            ? {
                                                                width: parseFloat(formatSize(selectedSize.width + 4.723)),
                                                                height: parseFloat(formatSize(selectedSize.height + 4.723)),
                                                                unit,
                                                            }
                                                            : selectedProduct === "MONTAGE" ||
                                                                selectedProduct === "ARCH" ||
                                                                selectedProduct === "PRINTS" ||
                                                                selectedProduct === "FMOUNTS" ||
                                                                selectedProduct === "HIPSTER" ||
                                                                selectedProduct === "HANDTORN" ||
                                                                selectedProduct === "FPRINTS"
                                                                ? { ...selectedSize, unit }
                                                                : {
                                                                    width: selectedSize.width * 2,
                                                                    height: selectedSize.height,
                                                                    unit
                                                                };

                                                    // --- Determine spreads ---
                                                    const spreads =
                                                        selectedProduct === "MONTAGE" ||
                                                            selectedProduct === "CANVAS" ||
                                                            selectedProduct === "FCANVAS"
                                                            ? 1
                                                            : selectedPageCount;

                                                    // --- Pass data to parent for creation ---
                                                    onSelect(
                                                        selectedProduct,
                                                        parsedSize,
                                                        spreads,
                                                        backgroundColor,
                                                        albumName,
                                                        cutArea,
                                                        unit,
                                                        spineWidth,
                                                         //  NEW
                                                    );
                                                } catch (err: any) {
                                                    setDialogMessage(err.message || 'Unknown error');
                                                    setDialogOpen(true);
                                                }
                                            }}
                                        >
                                            Create Product
                                        </Button>
                                    </div>
                                </div>

                                {/* Message dialog for general errors */}
                                <MessageDialog
                                    open={dialogOpen}
                                    message={dialogMessage}
                                    onClose={() => setDialogOpen(false)}
                                />
                            </div>
     
              </section>    
            </main>
            {/* BOTTOM WHITE WRAPPER*/}
            <main className="main-content">
            <section className="bottom-white-wrapper">
                <div className="bottom-wrapper-title-wrapper">
                    <h2 className="bottom-wrapper-title">Existing Products</h2>
                </div>

                <div className="albums-list-container">
                    {token ? (
                        <AlbumListPanel
                            token={token}
                            onOpenProject={handleOpenProject}
                            refreshTrigger={refreshTrigger}
                            onAlbumSaved={onAlbumSaved}
                        />
                    ) : (
                        <p>Please login to see your albums.</p>
                    )}
                </div>
                </section>
                  </main>
            </div>
            </div>
        </div>
    );
}